import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Inbox, CalendarDays, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUOTE_STATUSES = ["new", "contacted", "quoted", "closed"];
const BOOKING_STATUSES = ["scheduled", "confirmed", "completed", "cancelled"];

function StatusSelect({ testid, value, options, onChange }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-zinc-300 rounded-none text-xs font-sub uppercase tracking-wider px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#0057FF]"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem("titan_token")}`,
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [q, b] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers: headers() }),
        axios.get(`${API}/admin/bookings`, { headers: headers() }),
      ]);
      setQuotes(q.data);
      setBookings(b.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("titan_token");
        navigate("/admin/login");
        return;
      }
      setError("Could not load data. Try refreshing.");
    } finally {
      setLoading(false);
    }
  }, [headers, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("titan_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const updateStatus = async (kind, id, status) => {
    try {
      await axios.patch(`${API}/admin/${kind}/${id}`, { status }, { headers: headers() });
      if (kind === "quotes") {
        setQuotes((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      } else {
        setBookings((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      }
    } catch {
      setError("Status update failed.");
    }
  };

  const logout = () => {
    localStorage.removeItem("titan_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="admin-dashboard">
      <header className="bg-white border-b border-zinc-300 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="Anchor Moving logo" className="w-9 h-9 object-cover" />
            <p className="font-display uppercase text-lg leading-none">Anchor Moving <span className="text-[#0057FF]">/ Dispatch</span></p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="flex items-center gap-2 border border-zinc-300 bg-white px-4 py-2 text-xs font-sub uppercase tracking-[0.15em] hover:border-[#0057FF] hover:text-[#0057FF] transition-colors rounded-none"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-xs font-sub uppercase tracking-[0.15em] hover:bg-[#0057FF] transition-colors rounded-none"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 gap-px bg-zinc-300 border border-zinc-300 mb-8 max-w-md">
          <button
            data-testid="admin-tab-bookings"
            onClick={() => setTab("bookings")}
            className={`flex items-center justify-center gap-2 px-4 py-4 text-xs font-sub font-bold uppercase tracking-[0.15em] transition-colors rounded-none ${tab === "bookings" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:text-[#0057FF]"}`}
          >
            <CalendarDays className="w-4 h-4" /> Bookings ({bookings.length})
          </button>
          <button
            data-testid="admin-tab-quotes"
            onClick={() => setTab("quotes")}
            className={`flex items-center justify-center gap-2 px-4 py-4 text-xs font-sub font-bold uppercase tracking-[0.15em] transition-colors rounded-none ${tab === "quotes" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:text-[#0057FF]"}`}
          >
            <Inbox className="w-4 h-4" /> Quotes ({quotes.length})
          </button>
        </div>

        {error && <p data-testid="admin-error" className="mb-4 text-sm font-semibold text-red-600">{error}</p>}
        {loading && <p data-testid="admin-loading" className="font-sub uppercase tracking-[0.2em] text-xs text-zinc-500">Loading dispatch board…</p>}

        {!loading && tab === "bookings" && (
          <div className="overflow-x-auto border border-zinc-300 bg-white" data-testid="admin-bookings-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-300 bg-zinc-50 text-left">
                  {["Customer", "Contact", "Date", "Slot", "Type", "Route", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 font-sub text-xs uppercase tracking-[0.15em] text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-400 font-sub uppercase text-xs tracking-[0.2em]" data-testid="admin-bookings-empty">No bookings yet</td></tr>
                )}
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-zinc-200 hover:bg-zinc-50 transition-colors" data-testid={`admin-booking-row-${b.id}`}>
                    <td className="px-4 py-3 font-semibold">{b.name}<span className="block text-xs text-zinc-400 font-normal">{b.home_size}</span></td>
                    <td className="px-4 py-3 text-zinc-600">{b.email}<span className="block text-xs">{b.phone}</span></td>
                    <td className="px-4 py-3 font-sub font-bold">{b.move_date}</td>
                    <td className="px-4 py-3">{b.time_slot}</td>
                    <td className="px-4 py-3">{b.move_type}</td>
                    <td className="px-4 py-3 text-zinc-600 max-w-[220px]">{b.from_address} → {b.to_address}</td>
                    <td className="px-4 py-3">
                      <StatusSelect testid={`admin-booking-status-${b.id}`} value={b.status} options={BOOKING_STATUSES} onChange={(s) => updateStatus("bookings", b.id, s)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && tab === "quotes" && (
          <div className="overflow-x-auto border border-zinc-300 bg-white" data-testid="admin-quotes-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-300 bg-zinc-50 text-left">
                  {["Customer", "Contact", "Type", "Route", "Message", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 font-sub text-xs uppercase tracking-[0.15em] text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {quotes.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-400 font-sub uppercase text-xs tracking-[0.2em]" data-testid="admin-quotes-empty">No quote requests yet</td></tr>
                )}
                {quotes.map((q) => (
                  <tr key={q.id} className="border-b border-zinc-200 hover:bg-zinc-50 transition-colors" data-testid={`admin-quote-row-${q.id}`}>
                    <td className="px-4 py-3 font-semibold">{q.name}</td>
                    <td className="px-4 py-3 text-zinc-600">{q.email}<span className="block text-xs">{q.phone}</span></td>
                    <td className="px-4 py-3">{q.move_type}</td>
                    <td className="px-4 py-3 text-zinc-600 max-w-[220px]">{q.from_address} → {q.to_address}</td>
                    <td className="px-4 py-3 text-zinc-600 max-w-[240px]">{q.message || "—"}</td>
                    <td className="px-4 py-3">
                      <StatusSelect testid={`admin-quote-status-${q.id}`} value={q.status} options={QUOTE_STATUSES} onChange={(s) => updateStatus("quotes", q.id, s)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
